// src/pages/modals/CustomerModal.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  IconButton,
  Divider,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Stack,
  Autocomplete,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import api from '../../api/api';
import { cleanRut, validateRut, formatRut } from '../../utils/rut';

/* =========================
   RUT helpers
   ========================= */
function rutDv(bodyDigits) {
  let sum = 0;
  let mul = 2;

  for (let i = bodyDigits.length - 1; i >= 0; i--) {
    sum += parseInt(bodyDigits[i], 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }

  const mod = 11 - (sum % 11);
  if (mod === 11) return '0';
  if (mod === 10) return 'K';
  return String(mod);
}

// Acepta: 82935290 | 8.293.529-0 | 8293529-0
function parseRutLoose(raw) {
  const s = (raw || '').trim();
  if (!s) return null;

  const cleaned = s.replace(/[^0-9kK.\-]/g, '');
  if (!cleaned) return null;

  let body = '';
  let dv = '';

  if (cleaned.includes('-')) {
    const [a, b] = cleaned.split('-');
    body = (a || '').replace(/\./g, '');
    dv = (b || '').replace(/\./g, '').toUpperCase();
  } else {
    const compact = cleaned.replace(/\./g, '').toUpperCase();
    if (compact.length < 8) return null;
    body = compact.slice(0, -1);
    dv = compact.slice(-1);
  }

  body = (body || '').replace(/\D/g, '');
  dv = (dv || '').replace(/[^0-9K]/g, '').toUpperCase();

  if (body.length < 7 || body.length > 8) return null;
  if (!dv || dv.length !== 1) return null;

  const normalized = `${body}-${dv}`;
  const dvOk = rutDv(body) === dv;

  const c = cleanRut(normalized);
  const pretty = c ? formatRut(c) : normalized;

  // Clave para endpoint SII: body+dv, ejemplo 780883294
  const siiKey = `${body}${dv}`;

  return {
    body,
    dv,
    normalized,
    pretty,
    dvOk,
    siiKey,
  };
}

function cleanText(value) {
  const s = String(value ?? '').trim();
  return s || '';
}

const getComunasList = () => api.get('/datos/comunas');
const getComunaDetail = (codigo) => api.get(`/datos/comunas/${codigo}`);

const PAISES = [
  'Chile',
  'Argentina',
  'Perú',
  'Bolivia',
  'Brasil',
  'Paraguay',
  'Uruguay',
  'Colombia',
  'México',
  'Estados Unidos',
  'España',
  'Otro',
];

const CustomerModal = ({
  open,
  onClose,

  customerSearchTerm,
  handleCustomerSearch,
  customers = [],
  totalClientes = 0,
  selectedCustomerIndex = -1,
  onKeyDown,
  handleSelectCustomer,
  inputRef,
}) => {
  const searchValue = customerSearchTerm ?? '';
  const parsedRut = useMemo(() => parseRutLoose(searchValue), [searchValue]);

  const [createMode, setCreateMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectingId, setSelectingId] = useState(null);

  const [errors, setErrors] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const notify = useCallback((msg, sev = 'success') => {
    setSnack({ open: true, msg, sev });
  }, []);

  const closeSnack = useCallback(() => {
    setSnack((s) => ({ ...s, open: false }));
  }, []);

  const [comunasList, setComunasList] = useState([]);
  const [loadingComunas, setLoadingComunas] = useState(false);
  const [loadingComunaDetail, setLoadingComunaDetail] = useState(false);

  const initialForm = useMemo(
    () => ({
      tipo_persona: 1,
      tiene_giro: false,

      rut: '',
      nombres: '',
      apellidos: '',
      razon_social: '',
      giro: '',

      email: '',
      email_fiscal: '',

      calle: '',
      numero: '',
      complemento: '',
      comuna_codigo: '',
      comuna: '',
      ciudad: '',
      region: '',
      pais: 'Chile',
    }),
    []
  );

  const [form, setForm] = useState(initialForm);

  const [touched, setTouched] = useState({});
  const touch = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const [siiLoading, setSiiLoading] = useState(false);
  const [siiFound, setSiiFound] = useState(false);

  const lastSiiKeyRef = useRef(null);

  const paisEsChile = useMemo(() => {
    return cleanText(form.pais).toLowerCase() === 'chile';
  }, [form.pais]);

  useEffect(() => {
    if (!open) return;

    setCreateMode(false);
    setSaving(false);
    setSelectingId(null);
    setErrors({});
    setForm(initialForm);
    setTouched({});
    setSiiLoading(false);
    setSiiFound(false);

    setComunasList([]);
    setLoadingComunas(false);
    setLoadingComunaDetail(false);

    lastSiiKeyRef.current = null;
  }, [open, initialForm]);

  useEffect(() => {
    if (!open || !createMode || !paisEsChile) {
      setLoadingComunas(false);
      return;
    }

    if (Array.isArray(comunasList) && comunasList.length > 0) {
      setLoadingComunas(false);
      return;
    }

    let cancelled = false;

    setLoadingComunas(true);

    getComunasList()
      .then((r) => {
        if (cancelled) return;

        const list = Array.isArray(r?.data)
          ? r.data
          : Array.isArray(r?.data?.comunas)
            ? r.data.comunas
            : [];

        setComunasList(list);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error('[CustomerModal] Error cargando comunas:', err);
        setComunasList([]);
        notify('No se pudo cargar el listado de comunas.', 'warning');
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingComunas(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, createMode, paisEsChile, comunasList, notify]);

  const setField = (name, value, markTouched = true) => {
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
    if (markTouched) touch(name);
  };

  const clearFieldsErrors = (...names) => {
    setErrors((e) => {
      const next = { ...e };
      names.forEach((name) => {
        next[name] = undefined;
      });
      return next;
    });
  };

  const getCustomerDisplayName = (cust) => {
    if (!cust) return '';
    if (cust.nombres && cust.apellidos) return `${cust.nombres} ${cust.apellidos}`;
    if (cust.razon_social) return cust.razon_social;
    return cust.fullname || cust.name || cust.nombres || '';
  };

  const showCreateCTA = useMemo(() => {
    return open && !createMode && customers.length === 0 && !!parsedRut;
  }, [open, createMode, customers.length, parsedRut]);

  const enterCreateMode = useCallback(() => {
    setCreateMode(true);
    setErrors({});
    setTouched({});
    setSiiFound(false);
    lastSiiKeyRef.current = null;

    if (parsedRut) {
      setForm((f) => ({ ...f, rut: parsedRut.pretty }));
    }
  }, [parsedRut]);

  const normalizeRutForSave = (v) => {
    const c = cleanRut(v || '');
    if (!c) return '';
    if (!validateRut(c)) return null;
    return formatRut(c);
  };

  const validateForm = () => {
    const e = {};
    const rutNorm = form.rut?.trim() ? normalizeRutForSave(form.rut) : '';

    if (!cleanText(form.rut)) {
      e.rut = 'RUT es obligatorio.';
    }

    if (form.rut?.trim() && rutNorm === null) {
      e.rut = 'RUT inválido.';
    }

    if (!form.tipo_persona) {
      e.tipo_persona = 'Tipo de persona es obligatorio.';
    }

    if (form.tipo_persona === 2) {
      if (!cleanText(form.razon_social)) {
        e.razon_social = 'Razón social es obligatoria.';
      }

      if (!cleanText(form.giro)) {
        e.giro = 'Giro es obligatorio.';
      }
    } else {
      if (!cleanText(form.nombres)) {
        e.nombres = 'Nombres es obligatorio.';
      }

      if (!cleanText(form.apellidos)) {
        e.apellidos = 'Apellidos es obligatorio.';
      }

      if (form.tiene_giro && !cleanText(form.giro)) {
        e.giro = 'Giro es obligatorio.';
      }
    }

    const email = cleanText(form.email);
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      e.email = 'Email inválido.';
    }

    if (!cleanText(form.calle)) {
      e.calle = 'Dirección es obligatoria.';
    }

    if (!cleanText(form.numero)) {
      e.numero = 'Número es obligatorio.';
    }

    if (!cleanText(form.comuna)) {
      e.comuna = 'Comuna es obligatoria.';
    }

    if (!cleanText(form.ciudad)) {
      e.ciudad = 'Ciudad es obligatoria.';
    }

    if (!cleanText(form.region)) {
      e.region = 'Región es obligatoria.';
    }

    if (!cleanText(form.pais)) {
      e.pais = 'País es obligatorio.';
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const rutNorm = form.rut?.trim() ? normalizeRutForSave(form.rut) : '';

    const payload = {
      tipo_persona: Number(form.tipo_persona),
      tiene_giro: !!form.tiene_giro,
      rut: rutNorm || undefined,

      nombres: cleanText(form.nombres) || undefined,
      apellidos: cleanText(form.apellidos) || undefined,
      razon_social: cleanText(form.razon_social) || undefined,
      giro: cleanText(form.giro).slice(0, 40) || undefined,

      email: cleanText(form.email) || undefined,
      email_fiscal: cleanText(form.email_fiscal) || undefined,

      activo: true,
    };

    Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
    return payload;
  };

  const buildBillingAddressPayload = () => {
    return {
      tipo: 'Facturación',
      calle: cleanText(form.calle),
      numero: cleanText(form.numero),
      complemento: cleanText(form.complemento) || null,
      comuna: cleanText(form.comuna),
      ciudad: cleanText(form.ciudad),
      region: cleanText(form.region),
      pais: cleanText(form.pais) || 'Chile',
    };
  };

  const fetchFullCustomerById = useCallback(async (id) => {
    const resp = await api.get(`/ventas_clientes/${Number(id)}`);
    const data = resp?.data || {};
    return data.cliente || data.customer || data.data || data;
  }, []);

  const selectCustomerSafely = useCallback(
    async (cust) => {
      const id = cust?.id ?? cust?.cliente_id ?? cust?.customer_id ?? null;

      if (!id) {
        handleSelectCustomer?.(cust);
        onClose?.();
        return;
      }

      if (selectingId) return;
      setSelectingId(Number(id));

      try {
        const full = await fetchFullCustomerById(id);

        if (full?.id) {
          handleSelectCustomer?.(full);
          onClose?.();
        } else {
          notify(
            'No se pudo cargar el detalle del cliente. Se usará el registro de la lista.',
            'warning'
          );
          handleSelectCustomer?.(cust);
          onClose?.();
        }
      } catch (err) {
        console.error('[CustomerModal] Error consultando cliente por ID:', err);
        notify(err?.response?.data?.error || 'Error consultando cliente', 'error');
      } finally {
        setSelectingId(null);
      }
    },
    [fetchFullCustomerById, handleSelectCustomer, notify, onClose, selectingId]
  );

  const selectedComunaOption = useMemo(() => {
    if (!Array.isArray(comunasList) || !comunasList.length) return null;

    const codigo = cleanText(form.comuna_codigo);
    const comuna = cleanText(form.comuna).toLowerCase();

    if (codigo) {
      const byCode = comunasList.find((o) => String(o?.codigo || '') === codigo);
      if (byCode) return byCode;
    }

    if (comuna) {
      return (
        comunasList.find(
          (o) => cleanText(o?.nombre).toLowerCase() === comuna
        ) || null
      );
    }

    return null;
  }, [comunasList, form.comuna_codigo, form.comuna]);

  const handleSelectComuna = async (opt) => {
    if (!opt) {
      setForm((f) => ({
        ...f,
        comuna_codigo: '',
        comuna: '',
        ciudad: '',
        region: '',
      }));

      setTouched((t) => ({
        ...t,
        comuna_codigo: true,
        comuna: true,
        ciudad: true,
        region: true,
      }));

      clearFieldsErrors('comuna', 'ciudad', 'region');
      return;
    }

    const codigo = String(opt.codigo || '').trim();

    if (!codigo) {
      setForm((f) => ({
        ...f,
        comuna_codigo: '',
        comuna: opt.nombre || '',
        ciudad: '',
        region: '',
      }));

      setTouched((t) => ({
        ...t,
        comuna_codigo: true,
        comuna: true,
        ciudad: true,
        region: true,
      }));

      clearFieldsErrors('comuna', 'ciudad', 'region');
      return;
    }

    setLoadingComunaDetail(true);

    try {
      const { data } = await getComunaDetail(codigo);

      const comunaNombre = data?.nombre || opt.nombre || '';

      const ciudadNombre =
        data?.ciudad?.nombre ||
        data?.ciudad_nombre ||
        data?.ciudad ||
        '';

      const regionNombre =
        data?.region?.nombre ||
        data?.region_nombre ||
        data?.region ||
        '';

      setForm((f) => ({
        ...f,
        comuna_codigo: codigo,
        comuna: comunaNombre,
        ciudad: ciudadNombre,
        region: regionNombre,
      }));

      setTouched((t) => ({
        ...t,
        comuna_codigo: true,
        comuna: true,
        ciudad: true,
        region: true,
      }));

      clearFieldsErrors('comuna', 'ciudad', 'region');
    } catch (err) {
      console.error('[CustomerModal] Error consultando detalle de comuna:', err);

      setForm((f) => ({
        ...f,
        comuna_codigo: codigo,
        comuna: opt.nombre || '',
        ciudad: '',
        region: '',
      }));

      setTouched((t) => ({
        ...t,
        comuna_codigo: true,
        comuna: true,
        ciudad: true,
        region: true,
      }));

      notify('No se pudo completar ciudad y región para la comuna seleccionada.', 'warning');
    } finally {
      setLoadingComunaDetail(false);
    }
  };

  const handleChangePais = (_, val) => {
    const nextPais = val || 'Chile';
    const esChile = cleanText(nextPais).toLowerCase() === 'chile';

    setForm((f) => ({
      ...f,
      pais: nextPais,
      comuna_codigo: '',
      comuna: '',
      ciudad: '',
      region: '',
    }));

    setTouched((t) => ({
      ...t,
      pais: true,
      comuna_codigo: true,
      comuna: true,
      ciudad: true,
      region: true,
    }));

    clearFieldsErrors('comuna', 'ciudad', 'region');

    if (!esChile) {
      setLoadingComunas(false);
      setLoadingComunaDetail(false);
    }
  };

  /* ============================================================
   * AUTORELLENO desde SII
   * GET /datos/emisores-sii/:key
   * - Solo cuando createMode === true
   * - Solo si el RUT es válido
   * - No pisa campos que el usuario ya tocó
   * - mail_intercambio NO rellena email principal; solo email_fiscal
   * ============================================================ */
  const applySiiData = useCallback(
    (siiData) => {
      if (!siiData || typeof siiData !== 'object') return;

      const razon =
        siiData.razon_social ||
        siiData.razonSocial ||
        siiData.nombre ||
        siiData.razon ||
        '';

      const giro =
        siiData.giro ||
        siiData.actividad ||
        siiData.actividad_economica ||
        siiData.descripcionActividad ||
        (Array.isArray(siiData.actividades)
          ? siiData.actividades?.[0]?.descripcion
          : '') ||
        '';

      const emailFiscal =
        siiData.mail_intercambio ||
        siiData.mailIntercambio ||
        siiData.email_fiscal ||
        siiData.correo_fiscal ||
        '';

      const calle =
        siiData.calle ||
        siiData.direccion ||
        siiData.direccion_fiscal ||
        siiData.direccionFiscal ||
        siiData.dir ||
        siiData.dirRecep ||
        '';

      const numero =
        siiData.numero ||
        siiData.nro ||
        siiData.numero_direccion ||
        siiData.numeroDireccion ||
        '';

      const complemento =
        siiData.complemento ||
        siiData.departamento ||
        siiData.oficina ||
        siiData.local ||
        '';

      const comuna =
        siiData.comuna ||
        siiData.comuna_fiscal ||
        siiData.comunaFiscal ||
        siiData.cmnaRecep ||
        '';

      const ciudad =
        siiData.ciudad ||
        siiData.ciudad_fiscal ||
        siiData.ciudadFiscal ||
        siiData.ciudadRecep ||
        '';

      const region =
        siiData.region ||
        siiData.region_fiscal ||
        siiData.regionFiscal ||
        '';

      const pais =
        siiData.pais ||
        siiData.pais_fiscal ||
        siiData.paisFiscal ||
        'Chile';

      const suggestedTipo = razon ? 2 : 1;

      setForm((prev) => {
        const next = { ...prev };

        if (!touched.tipo_persona && suggestedTipo === 2) {
          next.tipo_persona = 2;
          next.tiene_giro = true;
        }

        if (!touched.razon_social && razon) next.razon_social = razon;
        if (!touched.giro && giro) next.giro = giro;
        if (!touched.email_fiscal && emailFiscal) next.email_fiscal = emailFiscal;

        if (!touched.calle && calle) next.calle = calle;
        if (!touched.numero && numero) next.numero = numero;
        if (!touched.complemento && complemento) next.complemento = complemento;

        if (!touched.comuna && comuna) next.comuna = comuna;
        if (!touched.ciudad && ciudad) next.ciudad = ciudad;
        if (!touched.region && region) next.region = region;
        if (!touched.pais && pais) next.pais = pais;

        if (!touched.tiene_giro && (giro || next.giro)) {
          next.tiene_giro = true;
        }

        return next;
      });
    },
    [touched]
  );

  // Debounce manual para consultar SII
  useEffect(() => {
    if (!open || !createMode) return;

    const p = parseRutLoose(form.rut);
    const rutNorm = form.rut?.trim() ? cleanRut(form.rut) : '';

    if (!rutNorm || !validateRut(rutNorm) || !p?.siiKey) {
      setSiiFound(false);
      setSiiLoading(false);
      lastSiiKeyRef.current = null;
      return;
    }

    if (lastSiiKeyRef.current === p.siiKey) return;

    const t = setTimeout(async () => {
      lastSiiKeyRef.current = p.siiKey;
      setSiiLoading(true);
      setSiiFound(false);

      try {
        const resp = await api.get(`/datos/emisores-sii/${p.siiKey}`);
        const data = resp?.data || null;

        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          setSiiFound(true);
          applySiiData(data);
        } else {
          setSiiFound(false);
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setSiiFound(false);
        } else {
          console.error('[CustomerModal] Error consultando /datos/emisores-sii:', err);
          setSiiFound(false);
        }
      } finally {
        setSiiLoading(false);
      }
    }, 450);

    return () => clearTimeout(t);
  }, [open, createMode, form.rut, applySiiData]);

  const createClient = async () => {
    if (saving) return;
    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = buildPayload();

      const { data } = await api.post('/ventas_clientes', payload);
      const created = data?.cliente || data?.customer || data?.data || data;

      if (!created?.id) {
        notify('Cliente creado, pero la respuesta no trajo el ID.', 'warning');
        return;
      }

      const billingAddress = buildBillingAddressPayload();

      try {
        await api.post(`/ventas_clientes/${created.id}/direcciones`, billingAddress);
      } catch (errDir) {
        console.error('[CustomerModal] Cliente creado, pero falló la dirección:', errDir);
        notify(
          errDir?.response?.data?.error ||
            'Cliente creado, pero no se pudo guardar la dirección.',
          'warning'
        );
      }

      const full = await fetchFullCustomerById(created.id);

      notify('Cliente creado y seleccionado ✅', 'success');
      handleSelectCustomer?.(full?.id ? full : created);
      onClose?.();
    } catch (err) {
      console.error('[CustomerModal] Error creando cliente:', err);

      const apiError =
        err?.response?.data?.error ||
        err?.response?.data?.errors?.[0]?.msg ||
        'Error al crear cliente';

      notify(apiError, 'error');
    } finally {
      setSaving(false);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'Enter' && showCreateCTA) {
      e.preventDefault();
      enterCreateMode();
      return;
    }

    onKeyDown?.(e);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
        }}
      >
        <Typography variant="h6" component="div">
          {createMode ? 'Crear Cliente' : 'Seleccionar Cliente'}
        </Typography>

        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!createMode && (
          <TextField
            label="Buscar RUT, nombre o razón social"
            value={searchValue}
            onChange={handleCustomerSearch}
            onKeyDown={onSearchKeyDown}
            inputRef={inputRef}
            fullWidth
            sx={{ mb: 1 }}
            autoFocus
          />
        )}

        {!createMode && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {totalClientes} cliente{totalClientes !== 1 ? 's' : ''} encontrado
              {totalClientes !== 1 ? 's' : ''}
            </Typography>

            {customers.length === 0 ? (
              <Box>
                <Alert severity="info" sx={{ mb: 1 }}>
                  Sin resultados.
                </Alert>

                {showCreateCTA && (
                  <Box>
                    <Button
                      type="button"
                      variant="contained"
                      fullWidth
                      startIcon={<AddIcon />}
                      onClick={enterCreateMode}
                      sx={{
                        py: 1.2,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 800,
                      }}
                    >
                      Crear cliente con RUT {parsedRut.pretty}
                    </Button>

                    {!parsedRut.dvOk && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ display: 'block', mt: 0.7 }}
                      >
                        ⚠ DV no coincide con el calculado. Puede corregirlo antes de guardar.
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <List dense>
                {customers.map((cust, idx) => {
                  const id = cust?.id ?? cust?.cliente_id ?? cust?.customer_id ?? idx;
                  const isSelecting =
                    selectingId != null && Number(id) === Number(selectingId);

                  return (
                    <ListItem key={String(id)} disablePadding>
                      <ListItemButton
                        disabled={selectingId != null}
                        selected={selectedCustomerIndex === idx}
                        onClick={() => selectCustomerSafely(cust)}
                      >
                        <ListItemText
                          primary={
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography sx={{ fontSize: '0.92rem', fontWeight: 800 }}>
                                {getCustomerDisplayName(cust)}
                              </Typography>

                              {cust.rut && <Chip size="small" label={cust.rut} />}

                              {isSelecting && <CircularProgress size={16} />}
                            </Stack>
                          }
                          secondary={cust.giro ? `Giro: ${cust.giro}` : null}
                          primaryTypographyProps={{ component: 'div' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}

        {createMode && (
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.tipo_persona} required>
                <InputLabel>Tipo persona</InputLabel>
                <Select
                  label="Tipo persona"
                  value={form.tipo_persona}
                  onChange={(e) => {
                    const next = Number(e.target.value);

                    setField('tipo_persona', next);

                    if (next === 2) {
                      setField('tiene_giro', true);
                    }

                    if (next === 1) {
                      setField('tiene_giro', false);
                    }
                  }}
                >
                  <MenuItem value={1}>Persona Natural</MenuItem>
                  <MenuItem value={2}>Persona Jurídica</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="RUT"
                value={form.rut}
                onChange={(e) => setField('rut', e.target.value)}
                onBlur={() => {
                  const norm = form.rut?.trim() ? normalizeRutForSave(form.rut) : '';
                  if (norm) setField('rut', norm, false);
                }}
                error={!!errors.rut}
                helperText={errors.rut || 'Ej: 9.234.456-0'}
                fullWidth
                required
                autoFocus
              />

              <Box sx={{ mt: 0.6, display: 'flex', alignItems: 'center', gap: 1 }}>
                {siiLoading && (
                  <>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">
                      Consultando SII…
                    </Typography>
                  </>
                )}

                {!siiLoading && siiFound && (
                  <Chip size="small" color="success" label="Datos SII encontrados" />
                )}

                {!siiLoading &&
                  !siiFound &&
                  form.rut &&
                  cleanRut(form.rut) &&
                  validateRut(cleanRut(form.rut)) && (
                    <Chip size="small" variant="outlined" label="Sin datos en SII" />
                  )}
              </Box>
            </Grid>

            {form.tipo_persona === 1 ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nombres"
                    value={form.nombres}
                    onChange={(e) => setField('nombres', e.target.value)}
                    error={!!errors.nombres}
                    helperText={errors.nombres || ' '}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Apellidos"
                    value={form.apellidos}
                    onChange={(e) => setField('apellidos', e.target.value)}
                    error={!!errors.apellidos}
                    helperText={errors.apellidos || ' '}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.tiene_giro}
                        onChange={(e) => setField('tiene_giro', e.target.checked)}
                      />
                    }
                    label="Tiene giro"
                  />
                </Grid>

                {form.tiene_giro && (
                  <Grid item xs={12}>
                    <TextField
                      label="Giro"
                      value={form.giro}
                      onChange={(e) => {
                        const v = (e.target.value || '').slice(0, 40);
                        setField('giro', v);
                      }}
                      inputProps={{ maxLength: 40 }}
                      error={!!errors.giro}
                      helperText={errors.giro || 'Máx 40 caracteres'}
                      fullWidth
                      required
                    />
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Razón social"
                    value={form.razon_social}
                    onChange={(e) => setField('razon_social', e.target.value)}
                    error={!!errors.razon_social}
                    helperText={errors.razon_social || ' '}
                    fullWidth
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Giro"
                    value={form.giro}
                    onChange={(e) => {
                      const v = (e.target.value || '').slice(0, 40);
                      setField('giro', v);
                    }}
                    inputProps={{ maxLength: 40 }}
                    error={!!errors.giro}
                    helperText={errors.giro || 'Máx 40 caracteres'}
                    fullWidth
                    required
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ mt: 0.5, mb: 0.5 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Dirección de facturación
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Dirección / calle"
                value={form.calle}
                onChange={(e) => setField('calle', e.target.value)}
                error={!!errors.calle}
                helperText={errors.calle || 'Ej: Av. Providencia'}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Número"
                value={form.numero}
                onChange={(e) => setField('numero', e.target.value)}
                error={!!errors.numero}
                helperText={errors.numero || ' '}
                fullWidth
                required
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label="Complemento"
                value={form.complemento}
                onChange={(e) => setField('complemento', e.target.value)}
                error={!!errors.complemento}
                helperText={errors.complemento || 'Opcional · Casa / Oficina / Dpto'}
                placeholder="Casa, oficina, departamento, local, referencia"
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {paisEsChile ? (
                <Autocomplete
                  options={comunasList}
                  loading={loadingComunas}
                  value={selectedComunaOption}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.codigo || '') === String(value?.codigo || '')
                  }
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    if (typeof option === 'string') return option;

                    const nombre = option.nombre || '';
                    const codigo = option.codigo || '';

                    return codigo ? `${nombre} (${codigo})` : nombre;
                  }}
                  onChange={(_, opt) => handleSelectComuna(opt)}
                  noOptionsText={loadingComunas ? 'Cargando comunas…' : 'Sin comunas disponibles'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Comuna"
                      error={!!errors.comuna}
                      helperText={errors.comuna || ' '}
                      fullWidth
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingComunas && (
                              <CircularProgress color="inherit" size={18} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  label="Comuna / localidad"
                  value={form.comuna}
                  onChange={(e) => setField('comuna', e.target.value)}
                  error={!!errors.comuna}
                  helperText={errors.comuna || ' '}
                  fullWidth
                  required
                />
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Ciudad"
                value={form.ciudad}
                onChange={(e) => setField('ciudad', e.target.value)}
                error={!!errors.ciudad}
                helperText={errors.ciudad || ' '}
                fullWidth
                disabled={paisEsChile}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Región / Estado"
                value={form.region}
                onChange={(e) => setField('region', e.target.value)}
                error={!!errors.region}
                helperText={errors.region || ' '}
                fullWidth
                disabled={paisEsChile}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                value={form.pais || 'Chile'}
                onChange={handleChangePais}
                options={PAISES}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="País"
                    error={!!errors.pais}
                    helperText={errors.pais || ' '}
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Email opcional"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email || ' '}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button
          type="button"
          onClick={() => {
            if (createMode) {
              setCreateMode(false);
              setErrors({});
            } else {
              onClose?.();
            }
          }}
          startIcon={createMode ? <ArrowBackIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
        >
          {createMode ? 'Volver' : 'Cerrar'}
        </Button>

        <Box sx={{ flex: 1 }} />

        {createMode && (
          <Button
            type="button"
            variant="contained"
            onClick={createClient}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            disabled={saving}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            Crear y seleccionar
          </Button>
        )}
      </DialogActions>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={closeSnack}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default CustomerModal;